import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import PhoneInput from "react-phone-input-2";
import { ShieldCheck, Gift, PhoneCall, CheckCircle } from "lucide-react";
import "react-phone-input-2/lib/style.css";

const CloseIcon = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ModalForm = ({ onClose }) => {
  const [formData, setFormData] = useState({ name: "", phone: "" });
  const [error, setError] = useState("");
  const [isSuccess, setIsSuccess] = useState(false); // состояние успеха
  const nameInputRef = useRef(null);

  useEffect(() => {
    setFormData({ name: "", phone: "" });
    nameInputRef.current?.focus();
  }, []);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[+]?[0-9]{10,15}$/;
    return phoneRegex.test(phone);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name) {
      setError("Пожалуйста, укажите ваше имя");
      return;
    }

    if (!validatePhone(formData.phone)) {
      setError("Пожалуйста, введите корректный номер телефона");
      return;
    }

    setError("");

    try {
      const response = await fetch('/api/submit-form/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (response.ok) {
        console.log('Данные успешно отправлены', data);
        setIsSuccess(true); // показываем окно успеха
      } else {
        console.error('Ошибка при отправке данных', data);
        setError('Произошла ошибка при отправке данных');
      }
    } catch (err) {
      console.error('Ошибка запроса', err);
      setError('Произошла ошибка при отправке запроса');
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center"
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="relative bg-white p-6 rounded-xl w-full max-w-md shadow-lg"
          onClick={(e) => e.stopPropagation()}
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -50, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 p-1 rounded-full text-red-600 hover:text-red-700 bg-transparent border-0 focus:outline-none"
            aria-label="Закрыть"
          >
            <CloseIcon className="w-6 h-6" />
          </button>

          {!isSuccess ? (
            <>
              <h2 className="text-2xl font-semibold mb-4 text-center text-red-600">Заказать звонок</h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <input
                    ref={nameInputRef}
                    type="text"
                    name="name"
                    placeholder="Ваше имя"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>

                <div>
                  <PhoneInput
                    country="ru"
                    value={formData.phone}
                    onChange={(phone) => setFormData({ ...formData, phone })}
                    inputProps={{
                      name: "phone",
                      required: true,
                    }}
                    containerClass="w-full"
                    inputStyle={{
                      width: "100%",
                      paddingLeft: "50px",
                      paddingRight: "16px",
                      paddingTop: "12px",
                      paddingBottom: "12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "0.5rem",
                      fontSize: "16px",
                    }}
                    buttonStyle={{
                      border: "none",
                      background: "transparent",
                    }}
                  />
                </div>

                {error && <p className="text-red-500 text-sm text-center">{error}</p>}

                <button
                  type="submit"
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg text-lg font-semibold transition-all duration-300 shadow-lg"
                >
                  Отправить
                </button>

                <p className="text-xs text-center text-gray-500">
                  Мы не передаём данные третьим лицам. Нажимая кнопку, вы соглашаетесь с{" "}
                  <a href="/privacy" className="text-red-600">
                    политикой конфиденциальности
                  </a>
                  .
                </p>

                <div className="flex justify-between mt-6">
                  <div className="flex flex-col items-center">
                    <PhoneCall className="w-8 h-8 text-red-600 mb-2" />
                    <p className="text-sm">Ответим за 30 секунд</p>
                  </div>
                  <div className="flex flex-col items-center">
                    <ShieldCheck className="w-8 h-8 text-green-600 mb-2" />
                    <p className="text-sm">ГОСТ и гарантия</p>
                  </div>
                  <div className="flex flex-col items-center">
                    <Gift className="w-8 h-8 text-yellow-500 mb-2" />
                    <p className="text-sm">Скидка 10% новым клиентам</p>
                  </div>
                </div>
              </form>
            </>
          ) : (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="flex flex-col items-center text-center"
            >
              <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
              <h3 className="text-2xl font-semibold mb-2">Спасибо за заявку!</h3>
              <p className="mb-6">Мы свяжемся с вами в ближайшее время.</p>
              {/* Кнопка "Закрыть" удалена */}
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ModalForm;
